import { V, V2d } from "../../core/Vector";
import { BaseEnemy } from "./BaseEnemy";

//=============================================================================
// FIGHTER CONFIGURATION - All tweakable values in one place
//=============================================================================

const FIGHTER_CONFIG = {
  // Basic stats
  health: 1, // Fragile - 1 hit kills
  mass: 0.8, // Lighter than basic enemies
  killPoints: 15, // More points than basic enemies

  // Movement
  maxSpeed: 100, // Maximum movement speed
  maxForce: 80, // Steering force strength
  arrivalThreshold: 5, // Distance to consider "arrived" at waypoint
  escapeDistance: 580, // Distance to self-destruct during retreat

  // Behavior
  dampingFactor: -0.5, // Damping for smooth movement

  // Visual
  strokeWidth: 1,
  shape: [V(0, -4), V(4, 4), V(-4, 4)],
} as const;

type FighterPhase = "approach" | "bombing" | "retreat";

//=============================================================================
// FIGHTER ENEMY CLASS - Squadron fighter with phase-based behavior
//=============================================================================

export class FighterEnemy extends BaseEnemy {
  // Configuration passed from squadron
  private waypoints: V2d[];

  // Behavior state
  private phase: FighterPhase = "approach";
  private waypointIndex = 0;
  private reachedFormation = false;

  constructor(
    position: V2d,
    waypoints: V2d[],
    color: number,
    munitionType: "bomb" | "missile",
  ) {
    super(position, {
      health: FIGHTER_CONFIG.health,
      killPoints: FIGHTER_CONFIG.killPoints,
      mass: FIGHTER_CONFIG.mass,
      visualConfig: {
        points: [...FIGHTER_CONFIG.shape],
        color: color,
        secondaryColor: color,
        strokeWidth: FIGHTER_CONFIG.strokeWidth,
      },
      movement: {
        maxSpeed: FIGHTER_CONFIG.maxSpeed,
        acceleration: FIGHTER_CONFIG.maxForce,
        damping: FIGHTER_CONFIG.dampingFactor,
      },
    });

    this.waypoints = waypoints;
  }

  //===========================================================================
  // MAIN BEHAVIOR - Phase-based squadron behavior
  //===========================================================================

  onTick(dt: number): void {
    if (!this.body) return;

    this.applyDamping();

    switch (this.phase) {
      case "approach":
        this.handleApproach();
        break;
      case "bombing":
        this.handleBombing(dt);
        break;
      case "retreat":
        this.handleRetreat();
        break;
    }
  }

  private handleApproach(): void {
    if (this.waypointIndex >= this.waypoints.length) {
      this.reachedFormation = true;
      return;
    }

    const currentPos = V(this.body!.position);
    const targetWaypoint = this.waypoints[this.waypointIndex];
    const distance = targetWaypoint.sub(currentPos).magnitude;

    if (distance < FIGHTER_CONFIG.arrivalThreshold) {
      this.waypointIndex++;
      if (this.waypointIndex >= this.waypoints.length) {
        this.reachedFormation = true;
      }
      return;
    }

    // Use base class seek method
    this.seekPosition(targetWaypoint, FIGHTER_CONFIG.arrivalThreshold * 3);

    // Face movement direction
    this.aimWithMovement();
  }

  private handleBombing(dt: number): void {
    // Stay in formation and face downward
    this.aimDown();

    // TODO: Add bombing logic if needed
  }

  private handleRetreat(): void {
    // Simple retreat - move away from center
    const currentPos = V(this.body!.position);
    const awayFromCenter = currentPos.normalize();
    const escapeTarget = awayFromCenter.mul(FIGHTER_CONFIG.escapeDistance);

    const distance = escapeTarget.sub(currentPos).magnitude;
    if (distance < 50) {
      this.destroy();
      return;
    }

    // Use base class seek method to escape
    this.seekPosition(escapeTarget);
  }

  //===========================================================================
  // SQUADRON INTERFACE - Called by FighterSquadron
  //===========================================================================

  setPhase(phase: FighterPhase): void {
    this.phase = phase;
  }

  setRetreatPath(waypoints: V2d[]): void {
    this.waypoints = waypoints;
    this.waypointIndex = 0;
  }

  hasReachedFormation(): boolean {
    return this.reachedFormation;
  }

  protected onDeath(): void {
    // Fighter destroyed - squadron will detect this automatically
  }
}
